import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage } from '@/lib/conflict';

interface PayLineItem {
  displayName: string;
  perCyclePrice: number;
  formatted: string;
}

interface PayLinkData {
  title: string;
  description?: string;
  coverageBullets: string[];
  lineItems: PayLineItem[];
  /** Present when the link is discounted; only the net percentage is shown. */
  discount?: {
    percent: number;
    label: string;
    amount: number;
    formattedAmount: string;
    formattedListAmount: string;
  } | null;
  currency: string;
  amount: number;
  formattedAmount: string;
  frequency: string;
  frequencyLabel: string;
  taxLabel: string;
  account: { name?: string; email?: string };
  expiresAt: string;
}

/**
 * Public payment-link landing page. Rendered outside auth — anyone with the
 * token can view the offer and pay. On pay we hit the backend to create the
 * gateway subscription and redirect to the hosted checkout.
 */
export default function PaymentLinkPage() {
  const { token } = useParams<{ token: string }>();
  const [search] = useSearchParams();
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);

  const status = search.get('status');

  const linkQuery = useQuery({
    queryKey: qk.paymentLink(token),
    queryFn: async () => {
      const res = await api.get(`/pay/${token}`);
      return res.data.data as PayLinkData;
    },
    // Returning from a successful checkout: the link is now used/completed, so
    // refetching would 410 and paint an error next to the success banner.
    enabled: Boolean(token) && status !== 'success',
  });

  const data = linkQuery.data ?? null;
  const loading = status !== 'success' && linkQuery.isPending;
  const error =
    checkoutError ??
    (linkQuery.isError
      ? apiErrorMessage(linkQuery.error, 'This payment link is unavailable.')
      : null);

  async function pay() {
    setPaying(true);
    setCheckoutError(null);
    try {
      const res = await api.post(`/pay/${token}/checkout`);
      const url = res.data?.data?.checkoutUrl;
      if (url) {
        window.location.href = url;
        return;
      }
      setCheckoutError('Could not start checkout. Please try again.');
    } catch (err) {
      setCheckoutError(apiErrorMessage(err, 'Checkout failed. Please try again.'));
    } finally {
      setPaying(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-lg rounded-2xl border bg-white p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2 text-sm font-semibold tracking-wide text-blue-600">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-600" /> TYASHIN
        </div>

        {loading && <p className="text-gray-500">Loading…</p>}

        {!loading && status === 'success' && (
          <div className="rounded-lg bg-green-50 p-4 text-sm text-green-800">
            Payment received — thank you! Your subscription is being activated. You can close this
            page.
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">{error}</div>
        )}

        {!loading && data && status !== 'success' && (
          <>
            <h1 className="text-xl font-bold text-gray-900">{data.title}</h1>
            {data.description && <p className="mt-2 text-sm text-gray-600">{data.description}</p>}

            {data.coverageBullets.length > 0 && (
              <div className="mt-5">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  What this covers
                </p>
                <ul className="space-y-1.5">
                  {data.coverageBullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-6 rounded-xl border bg-gray-50 p-4">
              {data.lineItems.map((li, i) => (
                <div key={i} className="flex justify-between py-1 text-sm text-gray-700">
                  <span>{li.displayName}</span>
                  <span>{li.formatted}</span>
                </div>
              ))}
              {data.discount && (
                <div className="flex justify-between py-1 text-sm font-medium text-green-700">
                  <span>Discount ({data.discount.label})</span>
                  <span>−{data.discount.formattedAmount}</span>
                </div>
              )}
              <div className="mt-2 flex items-baseline justify-between border-t pt-3">
                <span className="text-sm font-medium text-gray-500">
                  Total · {data.frequencyLabel}
                </span>
                <span className="text-2xl font-bold text-gray-900">{data.formattedAmount}</span>
              </div>
              <p className="mt-1 text-right text-xs text-gray-400">{data.taxLabel}</p>
            </div>

            {data.account?.name && (
              <p className="mt-4 text-xs text-gray-500">
                Billing account: <span className="font-medium">{data.account.name}</span>
                {data.account.email ? ` (${data.account.email})` : ''}
              </p>
            )}

            <button
              onClick={pay}
              disabled={paying}
              className="mt-6 w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {paying ? 'Redirecting to secure checkout…' : `Pay ${data.formattedAmount}`}
            </button>
            <p className="mt-3 text-center text-xs text-gray-400">
              You'll be billed {data.frequencyLabel.toLowerCase()} and can cancel anytime. Payments
              are processed securely by Razorpay / Stripe.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
